"use client";

import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { ADD_ENTRY_TO_MAIL_LIST } from "@/graphql/mutations";
import { CHECK_MAIL_IN_LIST, GET_POPUP } from "@/graphql/queries";
import Cookies from 'js-cookie';
import { AWS_URL } from "@/constants";

const POPUP_DISMISSED_KEY = 'signup_popup_dismissed';
const TWELVE_HOURS_MS = 12 * 60 * 60 * 1000;

function isDismissedRecently() {
  const raw = Cookies.get(POPUP_DISMISSED_KEY);
  if (!raw) return false;
  try {
    const { timestamp } = JSON.parse(raw);
    return Date.now() - timestamp < TWELVE_HOURS_MS;
  } catch {
    return false;
  }
}

function saveDismissedCookie() {
  Cookies.set(
    POPUP_DISMISSED_KEY,
    JSON.stringify({ timestamp: Date.now() }),
    { expires: 1 }
  );
}

const getImageUrl = (fileObj) => {
  if (!fileObj?.path) return null;
  return `${AWS_URL}${fileObj.path}`;
};

export default function SignupPopup() {
  const [isVisible, setIsVisible] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);
  const [email, setEmail] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState("");
  const [shouldSkip, setShouldSkip] = useState(false);

  const pathname = usePathname();
  const router = useRouter();

  const token = Cookies.get('authToken');
  const cookieEmail = Cookies.get('email');

  const [addToMailList, { loading: isSubmitting }] = useMutation(ADD_ENTRY_TO_MAIL_LIST);
  const [checkMailInList] = useLazyQuery(CHECK_MAIL_IN_LIST);

  const { data: popupData } = useQuery(GET_POPUP, {
    variables: { where: { type: { _eq: "POPUP" } } },
    fetchPolicy: "network-only",
  });

  const popupMeta = popupData
    ?.tenant_org_web_configuration
    ?.flatMap((c) => c.web_configuration_meta)
    ?.[0] ?? null;

  const popupImageSrc = getImageUrl(popupMeta?.file);

  // Use title and description directly from API — no regex, no hardcoding
  const offerTitle = popupMeta?.title ?? "";
  const offerDescription = popupMeta?.description ?? "";

  useEffect(() => {
    if (isDismissedRecently()) {
      setShouldSkip(true);
      return;
    }
    if (!token || !cookieEmail) return;
    checkMailInList({
      variables: { where: { mail: { _eq: cookieEmail } } }
    }).then(({ data }) => {
      const inMailList = data?.tenant_mail_list?.length > 0;
      const hasFirstOrder = data?.tenant_mail_list?.[0]?.first_order === true;
      if (inMailList || hasFirstOrder) setShouldSkip(true);
    }).catch(() => {});
  }, [token, cookieEmail]);

  useEffect(() => {
    if (pathname !== "/") return;
    if (hasTriggered) return;
    if (shouldSkip) return;

    const triggerPopup = () => {
      setHasTriggered(true);
      setIsVisible(true);
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(timeoutId);
    };

    const handleScroll = () => {
      const scrollPercentage =
        (window.scrollY /
          (document.documentElement.scrollHeight - window.innerHeight)) * 100;
      if (scrollPercentage >= 20) triggerPopup();
    };

    const timeoutId = setTimeout(triggerPopup, 10000);
    window.addEventListener("scroll", handleScroll);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [pathname, hasTriggered, shouldSkip]);

  const handleClose = () => {
    saveDismissedCookie();
    setIsVisible(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data: existing } = await checkMailInList({
        variables: { where: { mail: { _eq: email } } },
      });
      if (existing?.tenant_mail_list?.length > 0) {
        setError("This email is already registered.");
        return;
      }
      const { data } = await addToMailList({ variables: { mail: email } });
      if (data?.insert_tenant_mail_list?.affected_rows > 0) {
        setIsSuccess(true);
        saveDismissedCookie();
        setTimeout(() => {
          if (!token) {
            router.push("/register");
          } else {
            handleClose();
          }
        }, 1000);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch (err) {
      if (err.message?.includes("unique") || err.message?.includes("duplicate")) {
        setError("This email is already registered.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    }
  };

  if (!isVisible) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998]"
        onClick={handleClose}
      />

      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 pointer-events-none">
        <div
          className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl pointer-events-auto overflow-hidden flex"
          style={{ height: "520px" }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-20 w-8 h-8 flex items-center justify-center rounded-full hover:bg-black/10 text-gray-500 hover:text-gray-800 transition-all"
            aria-label="Close"
          >
            <X className="w-5 h-5" strokeWidth={2} />
          </button>

          {/* Left — API image */}
          <div className="w-1/2 flex-shrink-0 hidden sm:block relative">
            <img
              src={
                popupImageSrc ??
                "https://jewel-ecom.s3.ap-south-1.amazonaws.com/pub/development/picture/2026-01-24/7638ce38-b9a8-44dc-a347-468bff1673e9"
              }
              alt="Featured jewellery"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>

          {/* Right — Offer Copy from API */}
          <div className="flex-1 flex flex-col items-center justify-center px-10 py-12 text-center">
            {!isSuccess ? (
              <>
                <div className="mb-6">
                  <img
                    src="/logo-png.png"
                    alt="Logo"
                    className="h-10 w-auto mx-auto object-contain"
                  />
                </div>

                {/* Title from API — replaces all 3 hardcoded lines */}
                <h2 className="text-3xl font-bold text-[#4a3f35] leading-snug mb-6 whitespace-pre-line">
                  {offerTitle || "Loading offer..."}
                </h2>

                <div className="w-10 border-t border-[#c9b99a] mb-6" />

                <form onSubmit={handleSubmit} className="w-full space-y-3">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter Your Email*"
                    required
                    className="w-full px-4 py-3 border border-[#d6ccc0] rounded focus:outline-none focus:border-[#8b6f47] text-sm text-[#4a3f35] placeholder:text-[#b8a899] bg-white transition-all"
                  />
                  {error && (
                    <p className="text-xs text-red-500 text-left">{error}</p>
                  )}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#4a3f35] hover:bg-[#6b5d52] text-white py-3 rounded text-sm font-semibold tracking-widest uppercase transition-all disabled:opacity-50"
                  >
                    {isSubmitting ? "Please wait..." : "Continue"}
                  </button>
                </form>

                <button
                  onClick={handleClose}
                  className="mt-4 text-xs text-[#a89880] underline underline-offset-2 hover:text-[#6b5d52] transition-colors"
                >
                  No, thanks
                </button>
                <p className="text-[10px] text-[#b8a899] mt-2">
                  *Terms and conditions apply
                </p>
              </>
            ) : (
              <div className="py-4">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-7 h-7 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-[#4a3f35] mb-2">You're in!</h3>
                {/* Description from API */}
                <p className="text-sm text-[#8b7355]">
                  {offerDescription}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}